import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClientService } from '../httpClient/http-client.service';
import { AssistContext } from './assist-context.service';

export interface AssistSource {
  endpoint: string;
  params: Record<string, any>;
}

export interface AssistAnswer {
  answer: string;
  intent: string;
  slots: any;
  classifier: 'model' | 'keywords';
  scope: { universityId: string | null; intake: string | null };
  data: any;
  sources: AssistSource[];
  refused?: boolean;
}

/** A change the assistant has worked out but has NOT made. */
export interface AssistPlan {
  ok: boolean;
  /** Set when ok is false - what is missing or why it was refused. */
  reason?: string;
  missing?: 'university' | 'intake' | 'countries' | 'action' | null;
  planId?: string;
  summary?: string;
  action?: 'open' | 'close';
  universityName?: string | null;
  intakeName?: string | null;
  countries?: string[];
  courseLevelName?: string | null;
  affected?: number;
  sample?: string[];
  warnings?: string[];
  /** The signed-in person, resolved server-side - never sent from here. */
  requestedBy?: string | null;
  approvalPrompt?: string;
}

export interface AssistApplied {
  planId: string;
  applied: boolean;
  affected: number;
  approvedBy: string | null;
  appliedAt: string;
  backupId: string | null;
  summary: string;
}

/**
 * Calls the assistant.
 *
 * Every figure in a reply came from one of the endpoints listed in `sources`,
 * which the UI shows, so an answer can always be traced back to the data it
 * came from.
 *
 * The change flow is deliberately two calls. `plan` writes nothing and returns
 * an id; `apply` sends back only that id, so the browser cannot widen a change
 * after it has been shown and approved - the server still holds the course
 * list it built.
 */
@Injectable({ providedIn: 'root' })
export class AssistApiService {
  private readonly http = inject(HttpClientService);

  ask(question: string, context: AssistContext): Observable<any> {
    return this.http.post('v1/api/assist/ask', { question, context });
  }

  /** Works out what a change would do. Writes nothing. */
  plan(question: string, context: AssistContext): Observable<any> {
    return this.http.post('v1/api/assist/plan', { question, context });
  }

  /** Applies a plan. `confirm` is required by the server, not decoration. */
  apply(planId: string): Observable<any> {
    return this.http.post('v1/api/assist/apply', { planId, confirm: true });
  }

  /** Puts an applied change back, through the backup it took. */
  revert(planId: string): Observable<any> {
    return this.http.post('v1/api/assist/revert', { planId });
  }

  cancel(planId: string): Observable<any> {
    return this.http.post('v1/api/assist/cancel', { planId });
  }
}
